export const M9_MODULE_REVIEW = {
  id: "m9-module-review",
  title: "🎯 Module 9 Review - Complete Assessment",
  topic: "Comprehensive review of all Module 9 content through natural dialogues and real-world situations. Test your mastery of conditionals, relative clauses, modals, and more!",
  iconType: "clipboard-check",
  
  sets: [
    {
      id: "m9-mr-s1",
      title: "Set 1: At the Doctor's Office 🏥",
      context: `**Doctor's Appointment**

**Nurse:** "Good morning! You look unwell today. What's wrong?"

**Patient:** "I have a terrible headache and a sore throat. I might have the flu."

**Nurse:** "Let me check your temperature. If you have a fever, we'll need to run some tests."

**Patient:** "Do I have to stay in bed all week?"

**Nurse:** "You must rest, but you don't have to stay in bed constantly. When you feel better, you can move around."

**Doctor enters:** "Hello! I'm Dr. Martinez, who has been working here for ten years. Let me examine you."

**Patient:** "My symptoms started yesterday. I couldn't sleep because of the pain."

**Doctor:** "You have tonsillitis. I'm prescribing medication that should help. You must not skip any doses."

**Patient:** "Unless I take the medicine, I won't recover quickly, will I?"

**Doctor:** "Exactly! If you follow the treatment, you'll feel better in three days."`,
      
      questions: [
        {
          type: "mc",
          q: "What does the patient think they might have?",
          options: [
            "The flu",
            "A broken bone",
            "Tonsillitis"
          ],
          a: "The flu",
          tip: "'Might' shows possibility!",
          successtip: "✓ MIGHT! Recognized uncertain possibility! 🤔"
        },
        {
          type: "mc",
          q: "'If you have a fever, we'll need to run some tests' is:",
          options: [
            "First conditional (real future possibility)",
            "Second conditional (hypothetical)",
            "Zero conditional (general truth)"
          ],
          a: "First conditional (real future possibility)",
          successtip: "✓ FIRST CONDITIONAL! Real medical procedure! 🌡️"
        },
        {
          type: "mc",
          q: "What is the difference between 'must rest' and 'don't have to stay in bed'?",
          options: [
            "'Must' = obligation, 'don't have to' = not necessary",
            "They mean the same thing",
            "'Must' = suggestion, 'don't have to' = prohibition"
          ],
          a: "'Must' = obligation, 'don't have to' = not necessary",
          successtip: "✓ MODALS! Obligation vs no obligation! 📋"
        },
        {
          type: "mc",
          q: "The clause 'who has been working here for ten years' is:",
          options: [
            "Non-defining (extra info about Dr. Martinez)",
            "Defining (which doctor)",
            "A question tag"
          ],
          a: "Non-defining (extra info about Dr. Martinez)",
          tip: "Commas = extra info!",
          successtip: "✓ NON-DEFINING! Bonus background! 👨‍⚕️"
        },
        {
          type: "mc",
          q: "What does the patient actually have?",
          options: [
            "Tonsillitis",
            "The flu",
            "A headache only"
          ],
          a: "Tonsillitis",
          successtip: "✓ DIAGNOSIS! Final medical answer! 🏥"
        },
        {
          type: "mc",
          q: "'You must not skip any doses' means:",
          options: [
            "It's prohibited to skip doses",
            "It's not necessary to take medicine",
            "It's optional"
          ],
          a: "It's prohibited to skip doses",
          tip: "Must not = prohibition!",
          successtip: "✓ MUST NOT! Strong prohibition! ⚠️"
        },
        {
          type: "mc",
          q: "'Unless I take the medicine' means:",
          options: [
            "If I don't take the medicine",
            "When I take the medicine",
            "Because I take the medicine"
          ],
          a: "If I don't take the medicine",
          successtip: "✓ UNLESS! Perfect conversion! 💊"
        },
        {
          type: "mc",
          q: "The question tag 'will I?' is used because:",
          options: [
            "The statement is negative ('won't recover')",
            "The statement is positive",
            "It's a random choice"
          ],
          a: "The statement is negative ('won't recover')",
          tip: "Negative statement → positive tag!",
          successtip: "✓ QUESTION TAGS! Negative/positive pattern! ✅"
        },
        {
          type: "mc",
          q: "'If you follow the treatment, you'll feel better' is:",
          options: [
            "First conditional (real advice)",
            "Second conditional (impossible)",
            "Zero conditional (scientific fact)"
          ],
          a: "First conditional (real advice)",
          successtip: "✓ MEDICAL ADVICE! Real treatment plan! 💡"
        },
        {
          type: "mc",
          q: "Why couldn't the patient sleep?",
          options: [
            "Because of the pain",
            "Because of tests",
            "Because of the doctor"
          ],
          a: "Because of the pain",
          successtip: "✓ COMPREHENSION! Found the reason! 😴"
        },
        {
          type: "mc",
          q: "In how many days will the patient feel better?",
          options: [
            "Three days",
            "Ten days",
            "One week"
          ],
          a: "Three days",
          successtip: "✓ TIMELINE! Recovery period! 📅"
        },
        {
          type: "mc",
          q: "'Medication that should help' - this clause is:",
          options: [
            "Defining (which medication)",
            "Non-defining (extra info)",
            "Neither"
          ],
          a: "Defining (which medication)",
          tip: "No commas = essential info!",
          successtip: "✓ DEFINING! Specifies the medication! 💊"
        }
      ]
    },

    {
      id: "m9-mr-s2",
      title: "Set 2: Science Class Experiment 🔬",
      context: `**Chemistry Lab**

**Teacher:** "Good morning, class! Today we're doing an experiment that involves mixing chemicals."

**Student 1:** "When you mix these two substances, what happens?"

**Teacher:** "When you pour acid into water, it creates heat. This is a scientific fact. You must wear safety goggles at all times."

**Student 2:** "Do we have to use the microscope?"

**Teacher:** "No, you don't have to. But if you want to see the crystals forming, you should use it."

**Student 1:** "The mixture is being heated now. What's the next step?"

**Teacher:** "As soon as it boils, we'll add the salt. Then the solution will be cooled in this beaker."

**Student 2:** "If we heat it too much, will it burn?"

**Teacher:** "Yes! Unless you control the temperature carefully, the experiment could fail. That's why the data is recorded every minute."

**Student 1:** "This equipment, which was donated last year, is really advanced, isn't it?"

**Teacher:** "Exactly! The results are analyzed by computers that make the process faster."`,
      
      questions: [
        {
          type: "mc",
          q: "'When you pour acid into water, it creates heat' is:",
          options: [
            "Zero conditional (scientific fact)",
            "First conditional (future prediction)",
            "Second conditional (hypothetical)"
          ],
          a: "Zero conditional (scientific fact)",
          tip: "Present + present = always true!",
          successtip: "✓ ZERO CONDITIONAL! Scientific truth! 🔬"
        },
        {
          type: "mc",
          q: "What must students wear?",
          options: [
            "Safety goggles",
            "A microscope",
            "Crystals"
          ],
          a: "Safety goggles",
          tip: "Must = obligation!",
          successtip: "✓ MUST! Safety requirement! 🥽"
        },
        {
          type: "mc",
          q: "What's the difference between 'have to use' and 'don't have to use'?",
          options: [
            "'Don't have to' means it's optional, not required",
            "They mean the same",
            "'Don't have to' means it's prohibited"
          ],
          a: "'Don't have to' means it's optional, not required",
          successtip: "✓ MODAL DISTINCTION! Optional vs required! 📝"
        },
        {
          type: "mc",
          q: "'The mixture is being heated' is:",
          options: [
            "Present passive (action happening now)",
            "Active voice",
            "Past tense"
          ],
          a: "Present passive (action happening now)",
          tip: "Is being + past participle!",
          successtip: "✓ PASSIVE VOICE! Ongoing process! 🔥"
        },
        {
          type: "mc",
          q: "'As soon as it boils, we'll add the salt' - when will they add salt?",
          options: [
            "Immediately when it boils (time clause)",
            "Before it boils",
            "Maybe never"
          ],
          a: "Immediately when it boils (time clause)",
          successtip: "✓ TIME CLAUSE! Sequence of events! ⏰"
        },
        {
          type: "mc",
          q: "'The solution will be cooled' is:",
          options: [
            "Future passive",
            "Future active",
            "Present passive"
          ],
          a: "Future passive",
          successtip: "✓ FUTURE PASSIVE! Will be + past participle! 🧊"
        },
        {
          type: "mc",
          q: "'If we heat it too much, will it burn?' is:",
          options: [
            "First conditional question (real possibility)",
            "Second conditional (hypothetical)",
            "Zero conditional (scientific fact)"
          ],
          a: "First conditional question (real possibility)",
          successtip: "✓ CONDITIONAL QUESTION! Real concern! ❓"
        },
        {
          type: "mc",
          q: "'Unless you control the temperature carefully' means:",
          options: [
            "If you don't control the temperature carefully",
            "When you control the temperature",
            "Because you control it"
          ],
          a: "If you don't control the temperature carefully",
          successtip: "✓ UNLESS! Conditional warning! ⚠️"
        },
        {
          type: "mc",
          q: "'The data is recorded every minute' - who records it?",
          options: [
            "Not specified (passive emphasizes action, not doer)",
            "The teacher",
            "The students"
          ],
          a: "Not specified (passive emphasizes action, not doer)",
          tip: "Passive = focus on action!",
          successtip: "✓ PASSIVE PURPOSE! Action matters, not doer! 📊"
        },
        {
          type: "mc",
          q: "'This equipment, which was donated last year,' is:",
          options: [
            "Non-defining (extra info with commas)",
            "Defining (which equipment)",
            "A question tag"
          ],
          a: "Non-defining (extra info with commas)",
          successtip: "✓ NON-DEFINING! Bonus history! 📦"
        },
        {
          type: "mc",
          q: "The question tag 'isn't it?' is used because:",
          options: [
            "The statement is positive ('is really advanced')",
            "The statement is negative",
            "There's no reason"
          ],
          a: "The statement is positive ('is really advanced')",
          successtip: "✓ QUESTION TAG! Positive/negative pattern! ✅"
        },
        {
          type: "mc",
          q: "'Computers that make the process faster' - this clause is:",
          options: [
            "Defining (which computers)",
            "Non-defining (extra info)",
            "Neither"
          ],
          a: "Defining (which computers)",
          tip: "No commas = essential!",
          successtip: "✓ DEFINING! Specifies the computers! 💻"
        }
      ]
    },

    {
      id: "m9-mr-s3",
      title: "Set 3: Career Counseling Session 💼",
      context: `**Career Advisor's Office**

**Advisor:** "So, you're looking for a job that offers good opportunities. Tell me about yourself."

**Client:** "I'm someone who works hard and learns quickly. If I were you, what would you suggest?"

**Advisor:** "Well, if you had more experience, you could apply for senior positions. But right now, I'd recommend starting with an entry-level role."

**Client:** "I worked for a company that went bankrupt last year. That's why I'm here."

**Advisor:** "I see. People who lose jobs often feel discouraged, don't they? But you shouldn't give up."

**Client:** "What should I do to improve my chances?"

**Advisor:** "If you take this training course, you'll develop valuable skills. The program, which lasts three months, is very effective."

**Client:** "I don't have much money. Do I have to pay for it?"

**Advisor:** "You don't have to pay if you qualify for the scholarship. Unless you earn more than $40,000, you're eligible."

**Client:** "When I finish the course, will you help me find a job?"

**Advisor:** "Absolutely! As soon as you complete it, we'll start sending your resume to companies."`,
      
      questions: [
        {
          type: "mc",
          q: "'A job that offers good opportunities' - this clause is:",
          options: [
            "Defining (which kind of job)",
            "Non-defining (extra info)",
            "Neither"
          ],
          a: "Defining (which kind of job)",
          tip: "No commas = essential!",
          successtip: "✓ DEFINING! Specifies job type! 💼"
        },
        {
          type: "mc",
          q: "'I'm someone who works hard' - this clause is:",
          options: [
            "Defining (what kind of person)",
            "Non-defining (extra info)",
            "A question"
          ],
          a: "Defining (what kind of person)",
          successtip: "✓ DEFINING! Self-description! 💪"
        },
        {
          type: "mc",
          q: "'If I were you, what would you suggest?' is:",
          options: [
            "Second conditional (asking for advice)",
            "First conditional (real future)",
            "Zero conditional (general truth)"
          ],
          a: "Second conditional (asking for advice)",
          tip: "'If I were you' = advice!",
          successtip: "✓ ADVICE STRUCTURE! Classic second conditional! 💡"
        },
        {
          type: "mc",
          q: "'If you had more experience, you could apply' is:",
          options: [
            "Second conditional (hypothetical present)",
            "First conditional (real future)",
            "Third conditional (past)"
          ],
          a: "Second conditional (hypothetical present)",
          successtip: "✓ SECOND CONDITIONAL! Unreal present! 📋"
        },
        {
          type: "mc",
          q: "'A company that went bankrupt' - this clause is:",
          options: [
            "Defining (which company)",
            "Non-defining (extra info)",
            "Neither"
          ],
          a: "Defining (which company)",
          successtip: "✓ DEFINING! Identifies the company! 🏢"
        },
        {
          type: "mc",
          q: "The question tag 'don't they?' is used because:",
          options: [
            "The statement is positive ('feel discouraged')",
            "The statement is negative",
            "It's random"
          ],
          a: "The statement is positive ('feel discouraged')",
          successtip: "✓ QUESTION TAG! Checking agreement! ✅"
        },
        {
          type: "mc",
          q: "'You shouldn't give up' - what modal is this?",
          options: [
            "Should not (advice/recommendation)",
            "Must not (prohibition)",
            "Don't have to (not necessary)"
          ],
          a: "Should not (advice/recommendation)",
          successtip: "✓ SHOULD! Giving advice! 💭"
        },
        {
          type: "mc",
          q: "'If you take this training course, you'll develop valuable skills' is:",
          options: [
            "First conditional (real advice for future)",
            "Second conditional (hypothetical)",
            "Zero conditional (general truth)"
          ],
          a: "First conditional (real advice for future)",
          successtip: "✓ FIRST CONDITIONAL! Real plan! 📚"
        },
        {
          type: "mc",
          q: "'The program, which lasts three months,' is:",
          options: [
            "Non-defining (extra info with commas)",
            "Defining (which program)",
            "Neither"
          ],
          a: "Non-defining (extra info with commas)",
          successtip: "✓ NON-DEFINING! Duration detail! ⏰"
        },
        {
          type: "mc",
          q: "What's the difference between 'don't have to pay' and 'must not pay'?",
          options: [
            "'Don't have to' = optional, 'must not' = prohibited",
            "They mean exactly the same",
            "'Don't have to' = prohibited"
          ],
          a: "'Don't have to' = optional, 'must not' = prohibited",
          successtip: "✓ MODAL DIFFERENCE! Critical distinction! 💰"
        },
        {
          type: "mc",
          q: "'Unless you earn more than $40,000' means:",
          options: [
            "If you don't earn more than $40,000",
            "When you earn $40,000",
            "Because you earn money"
          ],
          a: "If you don't earn more than $40,000",
          successtip: "✓ UNLESS! Eligibility condition! 💵"
        },
        {
          type: "mc",
          q: "'As soon as you complete it, we'll start' - when will they start?",
          options: [
            "Immediately when course is finished",
            "Before the course",
            "Maybe never"
          ],
          a: "Immediately when course is finished",
          successtip: "✓ TIME CLAUSE! Sequence clarity! 🎯"
        }
      ]
    },

    {
      id: "m9-mr-s4",
      title: "Set 4: Business Meeting & Planning 🏢",
      context: `**Company Strategy Meeting**

**CEO:** "Thank you for coming. We need to discuss our new product, which launches next month."

**Manager 1:** "If we invest in marketing, we'll reach more customers. I really think we should do it."

**Manager 2:** "I came up with an idea. What if we partnered with influencers who have large followings?"

**CEO:** "That's interesting. Unless we differentiate ourselves, we won't compete effectively. We must not copy our competitors."

**Manager 1:** "Our research shows that when companies use social media, their sales increase. It's proven."

**Manager 2:** "The team, which has been working on this for months, is ready to launch. They've done a great job, haven't they?"

**CEO:** "Absolutely. People who buy our product want quality and innovation. If we deliver both, we'll succeed."

**Manager 1:** "What about pricing? If we lowered the price, would we sell more?"

**CEO:** "Maybe, but I wouldn't recommend it. We should focus on value instead."

**Manager 2:** "As soon as we finalize the budget, we can start the campaign."

**CEO:** "Perfect. Let's hold a meeting next week to review everything."`,
      
      questions: [
        {
          type: "mc",
          q: "'Our new product, which launches next month,' is:",
          options: [
            "Non-defining (extra timing info)",
            "Defining (which product)",
            "Neither"
          ],
          a: "Non-defining (extra timing info)",
          tip: "Commas = bonus detail!",
          successtip: "✓ NON-DEFINING! Launch date detail! 📅"
        },
        {
          type: "mc",
          q: "'If we invest in marketing, we'll reach more customers' is:",
          options: [
            "First conditional (real business strategy)",
            "Second conditional (hypothetical)",
            "Zero conditional (general truth)"
          ],
          a: "First conditional (real business strategy)",
          successtip: "✓ FIRST CONDITIONAL! Real plan! 📊"
        },
        {
          type: "mc",
          q: "Which phrasal verb means 'thought of an idea'?",
          options: [
            "Came up with",
            "Look up to",
            "Put up with"
          ],
          a: "Came up with",
          successtip: "✓ PHRASAL VERB! Creative thinking! 💡"
        },
        {
          type: "mc",
          q: "'Influencers who have large followings' - this clause is:",
          options: [
            "Defining (which influencers)",
            "Non-defining (extra info)",
            "Neither"
          ],
          a: "Defining (which influencers)",
          successtip: "✓ DEFINING! Specifies type of influencer! 📱"
        },
        {
          type: "mc",
          q: "'Unless we differentiate ourselves' means:",
          options: [
            "If we don't differentiate ourselves",
            "When we differentiate ourselves",
            "Because we differentiate"
          ],
          a: "If we don't differentiate ourselves",
          successtip: "✓ UNLESS! Business warning! ⚠️"
        },
        {
          type: "mc",
          q: "'We must not copy our competitors' means:",
          options: [
            "It's prohibited/strongly advised against copying",
            "It's not necessary to copy",
            "We should copy them"
          ],
          a: "It's prohibited/strongly advised against copying",
          tip: "Must not = prohibition!",
          successtip: "✓ MUST NOT! Strong prohibition! 🚫"
        },
        {
          type: "mc",
          q: "'When companies use social media, their sales increase' is:",
          options: [
            "Zero conditional (proven pattern)",
            "First conditional (future prediction)",
            "Second conditional (hypothetical)"
          ],
          a: "Zero conditional (proven pattern)",
          successtip: "✓ ZERO CONDITIONAL! Business truth! 📈"
        },
        {
          type: "mc",
          q: "'The team, which has been working on this for months,' is:",
          options: [
            "Non-defining (extra background info)",
            "Defining (which team)",
            "Neither"
          ],
          a: "Non-defining (extra background info)",
          successtip: "✓ NON-DEFINING! Team context! 👥"
        },
        {
          type: "mc",
          q: "The question tag 'haven't they?' is used because:",
          options: [
            "The statement is positive ('have done')",
            "The statement is negative",
            "It's random"
          ],
          a: "The statement is positive ('have done')",
          successtip: "✓ QUESTION TAG! Seeking agreement! ✅"
        },
        {
          type: "mc",
          q: "'People who buy our product' - this clause is:",
          options: [
            "Defining (which people - our customers)",
            "Non-defining (extra info)",
            "Neither"
          ],
          a: "Defining (which people - our customers)",
          successtip: "✓ DEFINING! Customer specification! 🛍️"
        },
        {
          type: "mc",
          q: "'If we lowered the price, would we sell more?' is:",
          options: [
            "Second conditional (hypothetical question)",
            "First conditional (real plan)",
            "Zero conditional (fact)"
          ],
          a: "Second conditional (hypothetical question)",
          tip: "Past tense + would = hypothetical!",
          successtip: "✓ SECOND CONDITIONAL! Exploring options! 💭"
        },
        {
          type: "mc",
          q: "Which collocation means 'organize/conduct a meeting'?",
          options: [
            "Hold a meeting",
            "Make a meeting",
            "Do a meeting"
          ],
          a: "Hold a meeting",
          successtip: "✓ COLLOCATION! Business English! 📋"
        }
      ]
    },

    {
      id: "m9-mr-s5",
      title: "Set 5: International Student Experience 🌍",
      context: `**Coffee Shop Conversation**

**Anna:** "Hi Maria! You're studying abroad, aren't you? How's it going?"

**Maria:** "It's amazing! The university, which is in London, has students from 80 countries."

**Anna:** "Wow! Do you have to speak English all the time?"

**Maria:** "Yes, I must speak English in classes. But I don't have to speak it with my roommate—she's Spanish too!"

**Anna:** "If you had known it would be this hard, would you still have come?"

**Maria:** "Absolutely! If I were you, I'd study abroad too. It's life-changing."

**Anna:** "I'm looking forward to visiting you. When I save enough money, I'll book a flight."

**Maria:** "Great! As soon as you arrive, we'll explore the city together."

**Anna:** "What should I see? Any recommendations?"

**Maria:** "If you visit London, you must see the museums. They're free! Unless you want to see special exhibitions, you don't have to pay anything."

**Anna:** "That's perfect. I could spend hours in museums. The British Museum sounds fascinating, doesn't it?"

**Maria:** "It is! People who love history adore it. And the Tate Modern, which displays contemporary art, is incredible too."`,
      
      questions: [
        {
          type: "mc",
          q: "The question tag 'aren't you?' is used because:",
          options: [
            "The statement is positive ('You're studying')",
            "The statement is negative",
            "It's a guess"
          ],
          a: "The statement is positive ('You're studying')",
          successtip: "✓ QUESTION TAG! Confirming information! ✅"
        },
        {
          type: "mc",
          q: "'The university, which is in London,' is:",
          options: [
            "Non-defining (extra location info)",
            "Defining (which university)",
            "Neither"
          ],
          a: "Non-defining (extra location info)",
          successtip: "✓ NON-DEFINING! Location detail! 🏛️"
        },
        {
          type: "mc",
          q: "What's the difference between 'must speak' and 'don't have to speak'?",
          options: [
            "'Must' = obligation, 'don't have to' = not required",
            "They mean the same thing",
            "'Don't have to' = prohibited"
          ],
          a: "'Must' = obligation, 'don't have to' = not required",
          successtip: "✓ MODAL CONTRAST! Required vs optional! 📚"
        },
        {
          type: "mc",
          q: "'If you had known it would be this hard, would you still have come?' is:",
          options: [
            "Third conditional (past hypothetical)",
            "Second conditional (present hypothetical)",
            "First conditional (future real)"
          ],
          a: "Third conditional (past hypothetical)",
          tip: "Had known + would have = past unreal!",
          successtip: "✓ THIRD CONDITIONAL! Past reflection! 💭"
        },
        {
          type: "mc",
          q: "'If I were you, I'd study abroad' is:",
          options: [
            "Second conditional (giving advice)",
            "First conditional (real plan)",
            "Third conditional (past)"
          ],
          a: "Second conditional (giving advice)",
          successtip: "✓ ADVICE! Classic recommendation structure! 💡"
        },
        {
          type: "mc",
          q: "Which phrasal verb means 'anticipating with excitement'?",
          options: [
            "Looking forward to",
            "Looking up to",
            "Running out of"
          ],
          a: "Looking forward to",
          successtip: "✓ PHRASAL VERB! Excitement expression! 🎉"
        },
        {
          type: "mc",
          q: "'When I save enough money, I'll book a flight' - what kind of clause is 'When I save'?",
          options: [
            "Time clause (subordinate clause showing sequence)",
            "Defining relative clause",
            "Non-defining clause"
          ],
          a: "Time clause (subordinate clause showing sequence)",
          successtip: "✓ TIME CLAUSE! Future sequence! ⏰"
        },
        {
          type: "mc",
          q: "'As soon as you arrive, we'll explore' - when will they explore?",
          options: [
            "Immediately when Anna arrives",
            "Before Anna arrives",
            "Maybe never"
          ],
          a: "Immediately when Anna arrives",
          successtip: "✓ AS SOON AS! Immediate sequence! 🚀"
        },
        {
          type: "mc",
          q: "'If you visit London, you must see the museums' - this is:",
          options: [
            "First conditional with imperative (strong advice)",
            "Second conditional (hypothetical)",
            "Zero conditional (fact)"
          ],
          a: "First conditional with imperative (strong advice)",
          tip: "Must = strong recommendation!",
          successtip: "✓ CONDITIONAL ADVICE! Strong suggestion! 🏛️"
        },
        {
          type: "mc",
          q: "'Unless you want to see special exhibitions' means:",
          options: [
            "If you don't want to see special exhibitions",
            "When you want to see exhibitions",
            "Because you want exhibitions"
          ],
          a: "If you don't want to see special exhibitions",
          successtip: "✓ UNLESS! Condition for free entry! 🎫"
        },
        {
          type: "mc",
          q: "'I could spend hours in museums' - what does 'could' express here?",
          options: [
            "Possibility/ability (I'm able to)",
            "Past action",
            "Prohibition"
          ],
          a: "Possibility/ability (I'm able to)",
          successtip: "✓ COULD! Expressing ability! 💪"
        },
        {
          type: "mc",
          q: "'The British Museum sounds fascinating, doesn't it?' - why this tag?",
          options: [
            "Positive statement needs negative tag",
            "Negative statement needs positive tag",
            "Random choice"
          ],
          a: "Positive statement needs negative tag",
          successtip: "✓ QUESTION TAG! Positive/negative balance! ✅"
        }
      ]
    }
  ]
};
